import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Save, Plus, Trash2, AlertTriangle, AlertCircle,
  CheckCircle2, Info, ShieldAlert, HelpCircle, Search, Upload, X, Filter, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { canViewCosts as canViewCostsRole } from '@/lib/roles';
import { formatCurrency, cn, formatBuildSku } from '@/lib/utils';
import { writeAuditLog } from '@/lib/audit';
import { toast } from 'sonner';
import { checkCompatibility, type CompatIssue, type ComponentWithSpecs } from '@/lib/compatibility';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { PricingTool } from '@/components/PricingTool';
import { HelpTooltip } from '@/components/HelpTooltip';

// ── Category ordering for the configurator slots ──────────────────────────────
const SLOTS = [
  { code: 'CPU', label: 'Processor', required: true },
  { code: 'FAN', label: 'CPU Cooling', required: false },
  { code: 'RAM', label: 'Memory', required: true },
  { code: 'MB',  label: 'Motherboard', required: true },
  { code: 'ROM', label: 'Storage', required: true },
  { code: 'GPU', label: 'Graphics Card', required: false },
  { code: 'PSU', label: 'Power Supply', required: true },
  { code: 'OS',  label: 'Operating System', required: false },
  { code: 'CAS', label: 'Case', required: true },
  { code: 'COO', label: 'Case Cooling', required: false },
  { code: 'ACC', label: 'Accessories', required: false },
] as const;

// Categories where specs are expected (not ACC/OS/COO)
const SPEC_EXPECTED_CATEGORIES = new Set(['CPU', 'FAN', 'MB', 'RAM', 'ROM', 'GPU', 'PSU', 'CAS']);

// Map configurator slot codes to component_types table codes
const SLOT_TO_TYPE_CODE: Record<string, string> = {
  CPU: 'CPU', FAN: 'FAN', MB: 'MB', RAM: 'RAM',
  ROM: 'STORAGE', GPU: 'GPU', PSU: 'PSU', CAS: 'CASE',
  OS: 'OS', COO: 'COO', ACC: 'ACC',
};

const DRAFT_KEY = 'configurator-draft';

// ── Component search within a category ────────────────────────────────────────
function SlotSearchInput({
  value,
  slotCode,
  specFilters,
  onChange,
}: {
  value: string;
  slotCode: string;
  specFilters?: Record<string, string>;
  onChange: (partNumber: string, description: string, cost: number, category: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 400, openUp: false });

  const filtersKey = useMemo(() => JSON.stringify(specFilters ?? {}), [specFilters]);
  const hasActiveFilters = specFilters ? Object.values(specFilters).some(v => v !== '') : false;

  useEffect(() => { setSearch(value); }, [value]);

  useEffect(() => {
    if (search.length < 2 && !hasActiveFilters) { setResults([]); return; }
    const timer = setTimeout(async () => {
      let query = supabase
        .from('components')
        .select('part_number, description, category, current_price, available_qty, status, build_sku, specs');
      if (search.length >= 2) {
        query = query.or(`part_number.ilike.%${search}%,description.ilike.%${search}%,build_sku.ilike.%${search}%`);
      }
      // ACC is a catch-all — don't filter by category so misc items are findable
      if (slotCode !== 'ACC') {
        query = query.eq('category', slotCode);
      }
      const { data } = await query.limit(80);
      let filtered = data || [];

      // Apply spec filters client-side
      if (specFilters) {
        const active = Object.entries(specFilters).filter(([_, v]) => v !== '');
        if (active.length > 0) {
          filtered = filtered.filter(c => {
            const specs = (c as any).specs || {};
            for (const [key, filterVal] of active) {
              const specVal = specs[key];
              if (specVal == null) return false;
              if (typeof specVal === 'boolean') {
                if (String(specVal) !== filterVal) return false;
              } else if (Array.isArray(specVal)) {
                if (!specVal.includes(filterVal)) return false;
              } else if (typeof specVal === 'number') {
                const minVal = parseFloat(filterVal);
                if (!isNaN(minVal) && specVal < minVal) return false;
              } else {
                if (String(specVal).toLowerCase() !== filterVal.toLowerCase()) return false;
              }
            }
            return true;
          });
        }
      }

      setResults(filtered);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, slotCode, filtersKey, hasActiveFilters]);

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 260;
      setDropdownPos({
        top: openUp ? rect.top - 2 : rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 400),
        openUp,
      });
    }
    setOpen(true);
  };

  return (
    <div className="flex-1">
      <input
        ref={inputRef}
        className="w-full border rounded px-3 py-2 text-sm font-mono bg-background"
        placeholder="Search part no. or description…"
        value={search}
        onChange={e => { setSearch(e.target.value); openDropdown(); }}
        onFocus={() => { if (search.length >= 2 || hasActiveFilters) openDropdown(); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 && (
        <div
          style={{
            position: 'fixed',
            [dropdownPos.openUp ? 'bottom' : 'top']: dropdownPos.openUp ? window.innerHeight - dropdownPos.top : dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          className="bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {results.map(c => (
            <button
              key={c.part_number}
              type="button"
              className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-gray-100 last:border-0 flex gap-3 items-center"
              onMouseDown={() => {
                setSearch(c.part_number);
                setOpen(false);
                onChange(c.part_number, c.description || '', c.current_price || 0, c.category || '');
              }}
            >
              <span className="font-mono font-semibold shrink-0 text-primary">{c.part_number}</span>
              <span className="text-muted-foreground truncate flex-1">{c.description}</span>
              {c.status && c.status !== 'Active' && (
                <span className="shrink-0 text-[10px] text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">{c.status}</span>
              )}
              <span className="shrink-0 text-muted-foreground tabular-nums">
                {c.available_qty != null ? `${c.available_qty}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Slot row types ────────────────────────────────────────────────────────────
interface SlotEntry {
  slotCode: string;
  partNumber: string;
  description: string;
  category: string;
  cost: number;
  qty: number;
  specs: Record<string, any> | null;
}

// ── Main ────────────────────────────────────────────────────────────────────
export function ConfiguratorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Load saved draft from localStorage
  const savedDraft = useMemo(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  // Build header
  const [headerForm, setHeaderForm] = useState<{ sku: string; description: string; comments: string; is_quoted: boolean; rrp: string }>(
    savedDraft?.header ?? { sku: '', description: '', comments: '', is_quoted: false, rrp: '' }
  );

  // SKU uniqueness check
  const [skuTaken, setSkuTaken] = useState(false);
  useEffect(() => {
    const sku = headerForm.sku.trim();
    if (!sku) { setSkuTaken(false); return; }
    const timer = setTimeout(async () => {
      const { count } = await supabase.from('builds').select('id', { count: 'exact', head: true }).eq('sku', sku);
      setSkuTaken((count ?? 0) > 0);
    }, 400);
    return () => clearTimeout(timer);
  }, [headerForm.sku]);

  // Slots
  const [entries, setEntries] = useState<SlotEntry[]>([]);
  const [draftLoaded, setDraftLoaded] = useState(false);

  const { data: currentUser } = useCurrentUser();
  const canViewCosts = canViewCostsRole(currentUser?.role);

  // ── Spec filter state ──────────────────────────────────────────────────
  const [specFilters, setSpecFilters] = useState<Record<string, Record<string, string>>>({});
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());
  const [collapsedSlots, setCollapsedSlots] = useState<Set<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('configurator.collapsedSlots') || '[]');
      return new Set(Array.isArray(saved) ? saved : []);
    } catch {
      return new Set();
    }
  });
  const toggleSlotCollapsed = (code: string) => {
    setCollapsedSlots(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      try { localStorage.setItem('configurator.collapsedSlots', JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const { data: componentTypes = [] } = useQuery({
    queryKey: ['component-types-schema'],
    queryFn: async () => {
      const { data } = await supabase
        .from('component_types')
        .select('code, specs_schema');
      return data ?? [];
    },
  });

  const specsSchemaMap = useMemo(() => {
    const map: Record<string, any[]> = {};
    componentTypes.forEach((t: any) => {
      map[t.code] = t.specs_schema?.fields ?? [];
    });
    return map;
  }, [componentTypes]);

  // ── Load existing build ─────────────────────────────────────────────────
  const [loadPickerOpen, setLoadPickerOpen] = useState(false);
  const [loadSearch, setLoadSearch] = useState('');
  const loadPickerRef = useRef<HTMLDivElement>(null);

  const { data: loadResults = [] } = useQuery({
    queryKey: ['configurator-load-search', loadSearch],
    queryFn: async () => {
      if (loadSearch.length < 2) return [];
      const { data } = await supabase
        .from('builds')
        .select('id, sku, description, status')
        .or(`sku.ilike.%${loadSearch}%,description.ilike.%${loadSearch}%`)
        .order('sku')
        .limit(15);
      return data ?? [];
    },
    enabled: loadSearch.length >= 2 && loadPickerOpen,
  });

  const loadBuild = async (buildId: string, buildSku: string, buildDescription: string) => {
    // Fetch BOM for this build
    const { data: bomRows } = await supabase
      .from('build_components')
      .select('component_sku, component_type, qty_per_build, cost_ex_vat')
      .eq('build_id', buildId);

    if (!bomRows || bomRows.length === 0) {
      toast.error('No components found for this build');
      return;
    }

    // Fetch component descriptions
    const skus = bomRows.map(r => r.component_sku).filter(Boolean);
    const descMap: Record<string, { description: string; category: string; cost: number }> = {};
    if (skus.length > 0) {
      const { data: compData } = await supabase
        .from('components')
        .select('part_number, description, category, current_price')
        .in('part_number', skus);
      (compData ?? []).forEach(c => {
        descMap[c.part_number] = { description: c.description || '', category: c.category || '', cost: c.current_price ?? 0 };
      });
    }

    // Map BOM rows to configurator entries
    const newEntries: SlotEntry[] = bomRows.map(r => {
      const comp = descMap[r.component_sku] || {};
      return {
        slotCode: (r.component_type || comp.category || 'ACC').toUpperCase(),
        partNumber: r.component_sku || '',
        description: comp.description || '',
        category: r.component_type || comp.category || '',
        cost: r.cost_ex_vat ?? comp.cost ?? 0,
        qty: r.qty_per_build || 1,
        specs: null,
      };
    });

    // Make sure all standard slots have at least one entry
    const usedSlots = new Set(newEntries.map(e => e.slotCode));
    for (const slot of SLOTS) {
      if (!usedSlots.has(slot.code)) {
        newEntries.push({
          slotCode: slot.code,
          partNumber: '',
          description: '',
          category: '',
          cost: 0,
          qty: 1,
          specs: null,
        });
      }
    }

    setEntries(newEntries);
    setHeaderForm(prev => ({
      ...prev,
      sku: '',
      description: buildDescription || '',
      comments: `Based on ${buildSku}`,
    }));
    setLoadPickerOpen(false);
    setLoadSearch('');
    toast.success(`Loaded BOM from ${buildSku} — enter a new SKU to save`);
  };

  // Fetch specs for selected components (for compatibility checking)
  const selectedSkus = entries.filter(e => e.partNumber).map(e => e.partNumber);

  const { data: componentSpecs } = useQuery({
    queryKey: ['configurator-specs', ...selectedSkus.sort()],
    queryFn: async () => {
      if (selectedSkus.length === 0) return [];
      const { data } = await supabase
        .from('components')
        .select('part_number, description, category, specs')
        .in('part_number', selectedSkus);
      return (data || []) as { part_number: string; description: string | null; category: string | null; specs: Record<string, any> | null }[];
    },
    enabled: selectedSkus.length > 0,
  });

  // Run compatibility checks + missing specs warnings
  const { compatIssues, missingSpecsSkus } = useMemo(() => {
    const missingSpecsSkus: string[] = [];
    if (!componentSpecs) return { compatIssues: [] as CompatIssue[], missingSpecsSkus };

    // Check for components with missing specs
    for (const c of componentSpecs) {
      if (!SPEC_EXPECTED_CATEGORIES.has(c.category?.toUpperCase() ?? '')) continue;
      const specs = c.specs || {};
      const filledKeys = Object.keys(specs).filter(k => specs[k] != null);
      if (filledKeys.length === 0) {
        missingSpecsSkus.push(c.part_number);
      }
    }

    if (componentSpecs.length < 2) return { compatIssues: [] as CompatIssue[], missingSpecsSkus };
    const withSpecs: ComponentWithSpecs[] = componentSpecs.map(c => ({
      part_number: c.part_number,
      description: c.description,
      category: c.category,
      specs: c.specs,
    }));
    return { compatIssues: checkCompatibility(withSpecs), missingSpecsSkus };
  }, [componentSpecs]);

  const errors = compatIssues.filter(i => i.severity === 'error');
  const warnings = compatIssues.filter(i => i.severity === 'warning');
  const infos = compatIssues.filter(i => i.severity === 'info');

  // Totals
  const totalCost = entries.reduce((sum, e) => sum + (e.cost * e.qty), 0);
  const filledSlots = entries.filter(e => e.partNumber).length;

  // ── Entry helpers ───────────────────────────────────────────────────────────
  const setEntry = (slotCode: string, index: number, updates: Partial<SlotEntry>) => {
    setEntries(prev => {
      const entriesForSlot = prev.filter(e => e.slotCode === slotCode);
      const others = prev.filter(e => e.slotCode !== slotCode);
      if (index < entriesForSlot.length) {
        entriesForSlot[index] = { ...entriesForSlot[index], ...updates };
      }
      return [...others, ...entriesForSlot];
    });
  };

  const addExtraSlot = (slotCode: string) => {
    setEntries(prev => [...prev, {
      slotCode,
      partNumber: '',
      description: '',
      category: '',
      cost: 0,
      qty: 1,
      specs: null,
    }]);
  };

  const removeEntry = (slotCode: string, index: number) => {
    setEntries(prev => {
      const entriesForSlot = prev.filter(e => e.slotCode === slotCode);
      const others = prev.filter(e => e.slotCode !== slotCode);
      entriesForSlot.splice(index, 1);
      return [...others, ...entriesForSlot];
    });
  };

  // Initialize entries — restore from draft if available, otherwise one empty per slot
  useEffect(() => {
    if (savedDraft?.entries?.length) {
      setEntries(savedDraft.entries);
      setDraftLoaded(true);
    } else {
      setEntries(SLOTS.map(s => ({
        slotCode: s.code,
        partNumber: '',
        description: '',
        category: '',
        cost: 0,
        qty: 1,
        specs: null,
      })));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft to localStorage on changes
  useEffect(() => {
    const hasContent = headerForm.sku || headerForm.description || entries.some(e => e.partNumber);
    if (hasContent) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ header: headerForm, entries }));
    }
  }, [headerForm, entries]);

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftLoaded(false);
  };

  // ── Create build mutation ───────────────────────────────────────────────────
  const createBuildMutation = useMutation({
    mutationFn: async () => {
      if (!headerForm.sku.trim()) throw new Error('Build SKU is required');

      const filledEntries = entries.filter(e => e.partNumber.trim());
      if (filledEntries.length === 0) throw new Error('Add at least one component');

      const { data, error } = await supabase
        .from('builds')
        .insert({
          sku: headerForm.sku.trim(),
          description: headerForm.description.trim(),
          status: 'Active',
          comments: headerForm.comments.trim() || null,
          is_quoted: headerForm.is_quoted,
          rrp: headerForm.rrp ? parseFloat(headerForm.rrp) : null,
          date_created: new Date().toISOString().split('T')[0],
        })
        .select('id, sku')
        .single();
      if (error) throw error;

      const { error: bomErr } = await supabase.from('build_components').insert(
        filledEntries.map(e => ({
          build_id: data.id,
          component_sku: e.partNumber,
          component_type: e.category || e.slotCode,
          qty_per_build: e.qty,
          cost_ex_vat: e.cost,
        }))
      );
      if (bomErr) throw bomErr;

      await writeAuditLog({
        action: 'build_created',
        entity_type: 'build',
        entity_id: data.id,
        entity_label: data.sku,
        new_value: { sku: data.sku, status: 'Active', components: filledEntries.length },
      });

      return data;
    },
    onSuccess: (data) => {
      clearDraft();
      queryClient.invalidateQueries({ queryKey: ['builds'] });
      toast.success(`Build ${data.sku} created`);
      // replace, not push: the draft is cleared above, so backing into the
      // configurator would only offer an empty one. Back belongs on the builds
      // list.
      navigate(`/builds/${data.id}`, { replace: true });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to create build'),
  });

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/builds')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Build Configurator</h1>
            <div className="relative" ref={loadPickerRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLoadPickerOpen(!loadPickerOpen)}
              >
                <Upload className="h-4 w-4 mr-1" />Load Existing Build
              </Button>
              {loadPickerOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => { setLoadPickerOpen(false); setLoadSearch(''); }} />
                  <div className="absolute top-full left-0 mt-1 z-50 w-96 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                          className="w-full pl-8 pr-8 h-9 text-sm border rounded-md bg-background px-3 py-1"
                          placeholder="Search by SKU or description..."
                          value={loadSearch}
                          onChange={e => setLoadSearch(e.target.value)}
                          autoFocus
                        />
                        {loadSearch && (
                          <button onClick={() => setLoadSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-60">
                      {loadSearch.length < 2 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">Type to search builds...</div>
                      ) : loadResults.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">No builds found</div>
                      ) : (
                        loadResults.map((b: any) => (
                          <button
                            key={b.id}
                            className="w-full text-left px-3 py-2.5 hover:bg-muted/50 text-sm border-b last:border-b-0 flex items-center gap-3"
                            onClick={() => loadBuild(b.id, b.sku, b.description || '')}
                          >
                            <div className="flex-1 min-w-0">
                              <span className="font-mono font-medium text-primary">{b.sku}</span>
                              <p className="text-xs text-muted-foreground truncate">{b.description || '—'}</p>
                            </div>
                            <Badge variant="outline" className="text-[10px] shrink-0">{b.status}</Badge>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <p className="text-muted-foreground text-sm mt-1">Select components and check compatibility before creating a build.</p>
        </div>
      </div>

      {/* Draft restored banner */}
      {draftLoaded && (
        <div className="flex items-center justify-between gap-3 text-sm bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
          <div className="flex items-center gap-2 text-blue-700">
            <Info className="h-4 w-4" />
            <span>Draft restored from your last session.</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-800 h-7 px-2"
            onClick={() => {
              clearDraft();
              setHeaderForm({ sku: '', description: '', comments: '', is_quoted: false, rrp: '' as string });
              setEntries(SLOTS.map(s => ({
                slotCode: s.code, partNumber: '', description: '', category: '', cost: 0, qty: 1, specs: null,
              })));
            }}
          >
            Clear draft
          </Button>
        </div>
      )}

      {/* Build info header */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Build Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">SKU *</label>
              <Input
                className={cn('font-mono', skuTaken && 'border-red-400 focus-visible:ring-red-400')}
                placeholder="e.g. AOI-1A-G4-R1"
                value={headerForm.sku}
                onChange={e => setHeaderForm(p => ({ ...p, sku: formatBuildSku(e.target.value) }))}
              />
              {skuTaken && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  A build with this SKU already exists
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <Input
                placeholder="Build description"
                value={headerForm.description}
                onChange={e => setHeaderForm(p => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Comments</label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm bg-background min-h-[40px]"
                placeholder="Optional notes..."
                value={headerForm.comments}
                onChange={e => setHeaderForm(p => ({ ...p, comments: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compatibility banner */}
      {(compatIssues.length > 0 || missingSpecsSkus.length > 0) && (
        <Card className={cn(
          'border-l-4',
          errors.length > 0 ? 'border-l-red-500 bg-red-50/50' :
          warnings.length > 0 ? 'border-l-yellow-500 bg-yellow-50/50' :
          missingSpecsSkus.length > 0 ? 'border-l-orange-400 bg-orange-50/50' :
          'border-l-blue-500 bg-blue-50/50'
        )}>
          <CardContent className="py-3">
            <div className="flex items-center gap-2 mb-2">
              {errors.length > 0 ? (
                <ShieldAlert className="h-5 w-5 text-red-600" />
              ) : warnings.length > 0 ? (
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              ) : missingSpecsSkus.length > 0 ? (
                <HelpCircle className="h-5 w-5 text-orange-500" />
              ) : (
                <Info className="h-5 w-5 text-blue-600" />
              )}
              <span className="font-semibold text-sm">
                Compatibility Check:
                {errors.length > 0 && ` ${errors.length} error${errors.length !== 1 ? 's' : ''}`}
                {warnings.length > 0 && `${errors.length > 0 ? ',' : ''} ${warnings.length} warning${warnings.length !== 1 ? 's' : ''}`}
                {missingSpecsSkus.length > 0 && `${(errors.length + warnings.length) > 0 ? ',' : ''} ${missingSpecsSkus.length} without specs`}
                {errors.length === 0 && warnings.length === 0 && missingSpecsSkus.length === 0 && ` ${infos.length} info`}
              </span>
            </div>
            <div className="space-y-1">
              {missingSpecsSkus.length > 0 && (
                <div className="flex items-start gap-2 text-sm">
                  <HelpCircle className="h-4 w-4 text-orange-500 shrink-0 mt-0.5" />
                  <span className="text-orange-800">
                    Missing specs — compatibility cannot be fully verified: <span className="font-mono text-xs">{missingSpecsSkus.join(', ')}</span>
                  </span>
                </div>
              )}
              {errors.map((issue, i) => (
                <div key={`e-${i}`} className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                  <span className="text-red-800">{issue.message}</span>
                  <span className="text-red-400 text-xs ml-auto shrink-0">{issue.components.join(', ')}</span>
                </div>
              ))}
              {warnings.map((issue, i) => (
                <div key={`w-${i}`} className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                  <span className="text-yellow-800">{issue.message}</span>
                  <span className="text-yellow-500 text-xs ml-auto shrink-0">{issue.components.join(', ')}</span>
                </div>
              ))}
              {infos.map((issue, i) => (
                <div key={`i-${i}`} className="flex items-start gap-2 text-sm">
                  <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                  <span className="text-blue-800">{issue.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Component slots */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Components <HelpTooltip tipKey="configurator.slots" /></CardTitle>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => {
                  const allCollapsed = SLOTS.every(s => collapsedSlots.has(s.code));
                  const next = allCollapsed ? new Set<string>() : new Set(SLOTS.map(s => s.code));
                  setCollapsedSlots(next);
                  try { localStorage.setItem('configurator.collapsedSlots', JSON.stringify([...next])); } catch {}
                }}
              >
                {SLOTS.every(s => collapsedSlots.has(s.code)) ? 'Expand all' : 'Collapse all'}
              </button>
              <span>{filledSlots} component{filledSlots !== 1 ? 's' : ''} selected</span>
              {canViewCosts && filledSlots > 0 && (
                <span className="font-mono font-semibold text-foreground">Total: {formatCurrency(totalCost)}</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {SLOTS.map(slot => {
              const slotEntries = entries.filter(e => e.slotCode === slot.code);
              const slotIssueSkus = slotEntries.map(e => e.partNumber).filter(Boolean);
              const slotErrors = errors.filter(i => i.components.some(c => slotIssueSkus.includes(c)));
              const slotWarnings = warnings.filter(i => i.components.some(c => slotIssueSkus.includes(c)));
              const slotMissingSpecs = slotIssueSkus.some(sku => missingSpecsSkus.includes(sku));

              const typeCode = SLOT_TO_TYPE_CODE[slot.code] || slot.code;
              const schemaFields = specsSchemaMap[typeCode] ?? [];
              const filterableFields = schemaFields.filter((f: any) =>
                ['select', 'multi_select', 'boolean', 'number'].includes(f.type)
              );
              const slotFilters = specFilters[slot.code] ?? {};
              const activeFilterCount = Object.values(slotFilters).filter(v => v !== '').length;
              const filtersVisible = openFilters.has(slot.code);
              const isCollapsed = collapsedSlots.has(slot.code);
              const collapsedSummary = slotEntries
                .filter(e => e.partNumber)
                .map(e => e.partNumber)
                .join(', ');

              return (
                <div
                  key={slot.code}
                  className={cn(
                    'px-4',
                    isCollapsed ? 'py-2' : 'py-3',
                    slotErrors.length > 0 ? 'bg-red-50/30' :
                    slotWarnings.length > 0 ? 'bg-yellow-50/30' : ''
                  )}
                >
                  {(() => {

                    return (
                      <>
                        <div className={cn('flex items-center gap-3', isCollapsed ? 'mb-0' : 'mb-2')}>
                          <button
                            type="button"
                            onClick={() => toggleSlotCollapsed(slot.code)}
                            className="flex items-center gap-1.5 shrink-0 hover:text-foreground transition-colors"
                            title={isCollapsed ? 'Expand' : 'Collapse'}
                          >
                            <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', isCollapsed && '-rotate-90')} />
                            <span className="text-xs font-semibold uppercase text-muted-foreground w-24 text-left">
                              {slot.label}
                            </span>
                          </button>
                          {isCollapsed && collapsedSummary && (
                            <span className="text-xs text-muted-foreground truncate" title={collapsedSummary}>
                              {collapsedSummary}
                            </span>
                          )}
                          {!isCollapsed && filterableFields.length > 0 && (
                            <button
                              className={cn(
                                'text-[11px] flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-muted transition-colors',
                                activeFilterCount > 0 ? 'text-primary font-medium' : 'text-muted-foreground'
                              )}
                              onClick={() => setOpenFilters(prev => {
                                const next = new Set(prev);
                                next.has(slot.code) ? next.delete(slot.code) : next.add(slot.code);
                                return next;
                              })}
                            >
                              <Filter className="h-3 w-3" />
                              <span>Filter</span> <HelpTooltip tipKey="configurator.specFilters" />
                              {activeFilterCount > 0 && (
                                <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5 ml-0.5">{activeFilterCount}</Badge>
                              )}
                            </button>
                          )}
                          {slot.required && !slotEntries.some(e => e.partNumber) && (
                            <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">Required</Badge>
                          )}
                          {slotErrors.length > 0 && (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          )}
                          {slotWarnings.length > 0 && !slotErrors.length && (
                            <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          )}
                          {slotMissingSpecs && !slotErrors.length && !slotWarnings.length && (
                            <span title="Missing specs — compatibility not fully checked"><HelpCircle className="h-4 w-4 text-orange-400" /></span>
                          )}
                          {slotEntries.some(e => e.partNumber) && !slotErrors.length && !slotWarnings.length && !slotMissingSpecs && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                        </div>

                        {!isCollapsed && filtersVisible && filterableFields.length > 0 && (
                          <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-2 ml-0 pl-0 py-2 px-3 bg-muted/30 rounded-md border border-muted">
                            {filterableFields.map((field: any) => {
                              const currentVal = slotFilters[field.key] ?? '';
                              const setVal = (v: string) => setSpecFilters(prev => ({
                                ...prev,
                                [slot.code]: { ...prev[slot.code], [field.key]: v },
                              }));

                              if (field.type === 'select' || field.type === 'multi_select') {
                                return (
                                  <div key={field.key} className="flex items-center gap-1">
                                    <label className="text-[10px] text-muted-foreground whitespace-nowrap">{field.label}</label>
                                    <select
                                      className="border rounded px-1.5 py-1 text-xs bg-background h-7"
                                      value={currentVal}
                                      onChange={e => setVal(e.target.value)}
                                    >
                                      <option value="">Any</option>
                                      {(field.options ?? []).map((opt: string) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  </div>
                                );
                              }

                              if (field.type === 'boolean') {
                                return (
                                  <div key={field.key} className="flex items-center gap-1">
                                    <label className="text-[10px] text-muted-foreground whitespace-nowrap">{field.label}</label>
                                    <select
                                      className="border rounded px-1.5 py-1 text-xs bg-background h-7"
                                      value={currentVal}
                                      onChange={e => setVal(e.target.value)}
                                    >
                                      <option value="">Any</option>
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  </div>
                                );
                              }

                              if (field.type === 'number') {
                                return (
                                  <div key={field.key} className="flex items-center gap-1">
                                    <label className="text-[10px] text-muted-foreground whitespace-nowrap">
                                      Min {field.label}
                                    </label>
                                    <input
                                      type="number"
                                      className="w-16 border rounded px-1.5 py-1 text-xs bg-background h-7"
                                      value={currentVal}
                                      onChange={e => setVal(e.target.value)}
                                      placeholder="—"
                                    />
                                  </div>
                                );
                              }

                              return null;
                            })}
                            {activeFilterCount > 0 && (
                              <button
                                className="text-[11px] text-destructive hover:underline self-center ml-1"
                                onClick={() => setSpecFilters(prev => ({ ...prev, [slot.code]: {} }))}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {!isCollapsed && slotEntries.map((entry, idx) => {
                    // Look up specs for this component
                    const compSpec = entry.partNumber
                      ? componentSpecs?.find(c => c.part_number === entry.partNumber)
                      : null;
                    const specs = compSpec?.specs ?? null;
                    const hasSchema = schemaFields.length > 0;
                    const hasAnySpecs = specs && Object.keys(specs).length > 0;

                    return (
                      <div key={`${slot.code}-${idx}`} className="mb-1">
                        <div className="flex items-center gap-2">
                          <SlotSearchInput
                            value={entry.partNumber}
                            slotCode={slot.code}
                            specFilters={specFilters[slot.code]}
                            onChange={(pn, desc, cost, cat) => {
                              setEntry(slot.code, idx, {
                                partNumber: pn,
                                description: desc,
                                cost,
                                category: cat,
                              });
                            }}
                          />
                          <span className="text-xs text-muted-foreground truncate max-w-[200px] hidden lg:inline" title={entry.description}>
                            {entry.description}
                          </span>
                          <input
                            type="number"
                            className="w-14 border rounded px-2 py-2 text-sm text-right bg-background"
                            min={1}
                            value={entry.qty}
                            onChange={e => setEntry(slot.code, idx, { qty: parseInt(e.target.value) || 1 })}
                            title="Quantity"
                          />
                          {canViewCosts && (
                            <input
                              type="number"
                              className="w-24 border rounded px-2 py-2 text-sm text-right bg-background font-mono"
                              step="0.01"
                              value={entry.cost}
                              onChange={e => setEntry(slot.code, idx, { cost: parseFloat(e.target.value) || 0 })}
                              title="Cost ex-VAT"
                            />
                          )}
                          {(slotEntries.length > 1 || idx > 0) ? (
                            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                              onClick={() => removeEntry(slot.code, idx)}>
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          ) : (
                            entry.partNumber ? (
                              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                                onClick={() => setEntry(slot.code, idx, { partNumber: '', description: '', cost: 0, category: '', specs: null })}>
                                <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                              </Button>
                            ) : <div className="w-8 shrink-0" />
                          )}
                        </div>
                        {entry.partNumber && hasSchema && (
                          <div className="text-[11px] mt-0.5 ml-1 leading-tight flex flex-wrap gap-x-1">
                            {!hasAnySpecs ? (
                              <span className="text-orange-500 flex items-center gap-1">
                                <span>⚠</span>
                                <span>No specs populated for this component</span>
                              </span>
                            ) : (
                              <>
                                {schemaFields.map((f: any, fi: number) => {
                                  const val = specs?.[f.key];
                                  const isMissing = val == null || val === '';
                                  let text: string;
                                  if (isMissing) {
                                    text = `${f.label}: —`;
                                  } else if (typeof val === 'boolean') {
                                    text = `${f.label}: ${val ? 'Yes' : 'No'}`;
                                  } else if (Array.isArray(val)) {
                                    text = `${f.label}: ${val.join(', ')}`;
                                  } else if (f.unit) {
                                    text = `${val} ${f.unit}`;
                                  } else {
                                    text = String(val);
                                  }
                                  return (
                                    <span key={f.key}>
                                      <span className={isMissing ? 'text-orange-500' : 'text-muted-foreground'}>
                                        {text}
                                      </span>
                                      {fi < schemaFields.length - 1 && <span className="text-muted-foreground/50"> · </span>}
                                    </span>
                                  );
                                })}
                                {schemaFields.some((f: any) => {
                                  const val = specs?.[f.key];
                                  return val == null || val === '';
                                }) && (
                                  <span className="text-orange-500 text-[10px] ml-1" title="Some specs are not populated">⚠</span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {!isCollapsed && ['RAM', 'ROM', 'COO', 'ACC'].includes(slot.code) && (
                    <button
                      className="text-xs text-primary hover:underline mt-1 flex items-center gap-1"
                      onClick={() => addExtraSlot(slot.code)}
                    >
                      <Plus className="h-3 w-3" /> Add another {slot.label.toLowerCase()}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Compatibility result when all clear */}
      {filledSlots >= 2 && compatIssues.length === 0 && missingSpecsSkus.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-medium">All selected components are compatible.</span>
        </div>
      )}

      {/* RRP / Pricing */}
      {canViewCosts && filledSlots > 0 && (
        <PricingTool
          totalCost={totalCost}
          rrpValue={headerForm.rrp}
          onRrpChange={rrp => setHeaderForm(p => ({ ...p, rrp }))}
          components={entries.filter(e => e.partNumber && e.description).map(e => ({
            partNumber: e.partNumber,
            description: e.description,
            category: e.slotCode,
            cost: e.cost * e.qty,
          }))}
          filledCount={filledSlots}
        />
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        <Button
          onClick={() => createBuildMutation.mutate()}
          disabled={createBuildMutation.isPending || !headerForm.sku.trim() || filledSlots === 0 || skuTaken}
        >
          <Save className="h-4 w-4 mr-2" />
          {createBuildMutation.isPending ? 'Creating...' : 'Create Build'}
        </Button>
        {errors.length > 0 && (
          <span className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="h-4 w-4" />
            {errors.length} compatibility error{errors.length !== 1 ? 's' : ''} — build can still be created
          </span>
        )}
        <Button variant="outline" onClick={() => navigate('/builds')}>Cancel</Button>
      </div>
    </div>
  );
}
